/**
 * Context menu for SideNav right-click — "Open in side pane."
 *
 * Works for ANY SideNav item with a path, not just tab-aware features: every
 * page in the app can be opened in the side pane. There is no "Open in tab" —
 * a plain click already opens-or-focuses the one primary tab for a place. A
 * caller can append its own row-specific actions via `extraItems`; they render
 * below a separator so the universal action always sits in the same place.
 */
import React from 'react';

import {
  CONTEXT_MENU_ITEM_CLASS,
  ContextMenuItemButtons,
  ContextMenuShell,
  type ContextMenuItem,
} from './ContextMenuShell';
import { useWorkspaceStore, tabsForGroup } from '../stores/useWorkspaceStore';

export type { ContextMenuItem } from './ContextMenuShell';

interface Props {
  title: string;
  path: string;
  x: number;
  y: number;
  onClose: () => void;
  /** Row-specific actions, shown after a separator; omitted or empty renders nothing extra. */
  extraItems?: ContextMenuItem[];
}

export const SideNavTabContextMenu: React.FC<Props> = ({
  title,
  path,
  x,
  y,
  onClose,
  extraItems,
}) => {
  const tabs = useWorkspaceStore(s => s.tabs);
  const addTab = useWorkspaceStore(s => s.addTab);
  const setActiveTabId = useWorkspaceStore(s => s.setActiveTabId);
  const setFocusedGroup = useWorkspaceStore(s => s.setFocusedGroup);

  const handleOpenInSidePane = () => {
    // Check if a tab with this path already exists in the secondary group.
    const secondaryTabs = tabsForGroup(tabs, 'secondary');
    const existing = secondaryTabs.find(t => t.path === path);
    if (existing) {
      setActiveTabId('secondary', existing.id);
      setFocusedGroup('secondary');
    } else {
      const id = `tab-${Date.now()}`;
      addTab({ id, title, path, pinned: false, group: 'secondary' });
      setActiveTabId('secondary', id);
      setFocusedGroup('secondary');
    }
    onClose();
  };

  return (
    <ContextMenuShell x={x} y={y} onClose={onClose}>
      <button
        type="button"
        role="menuitem"
        onClick={handleOpenInSidePane}
        className={CONTEXT_MENU_ITEM_CLASS}
      >
        Open in side pane
      </button>
      {extraItems && extraItems.length > 0 && (
        <>
          <div role="separator" className="border-border my-1 border-t" />
          <ContextMenuItemButtons items={extraItems} onClose={onClose} />
        </>
      )}
    </ContextMenuShell>
  );
};
